import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { themeColor } from 'utils/styles'
import { ERROR, TRUCK_SUBTYPE_42N } from 'utils/constants'
import USER_SETTINGS from 'constants/userSettings'
import { MSF } from '../../definitions/dashConstants'

import useApi from 'hooks/useApi'

import { fetchCustomSwitchGraphics } from 'actions/dash'
import { useSelector, useMakeSelector } from 'hooks/redux'
import {
  makeGetCustomSwitchGraphic,
  getVehicleSubType,
  getDashComponent,
} from 'selectors/dash'
import { getUserSetting } from 'selectors/user'

import { LoadingMeter, Pill, LoadingSwitchMeter } from 'components/patterns'

import {
  Part__Blank_MSF_43N as BlankMSF,
  Part__Blank_MSF_42N as BlankMSF42N,
  Part__Blank_43N_Switch as BlankSwitch,
  Part__Blank_42N_Switch as BlankSwitch42N,
  Part__Custom_TEM_Indicator as BlankCustomIndicator,
} from 'components/SwitchAndGauge/parts-components'

import PartNumberView from 'components/SwitchAndGauge/Dash/Part/PartNumberView'

export const PartGraphicCustom = ({
  location,
  capPartNumber,
  onClick,
  selected,
  hasChanged,
  isCustomIndicator,
  panelNumber,
  vehicleSubType: vehicleSubTypeFromLineNote,
  dimensions = {},
  showPill,
}) => {
  const showPartNumbers = useSelector(
    getUserSetting,
    USER_SETTINGS.SWITCH_AND_GAUGE_SHOW_PART_NUMBERS
  )
  const vehicleSubTypeFromDash = useSelector(getVehicleSubType)
  const component = useSelector(getDashComponent, location)
  const vehicleSubType = vehicleSubTypeFromLineNote || vehicleSubTypeFromDash
  const [{ error, doneLoading }, doFetch] = useApi(fetchCustomSwitchGraphics)

  const base64Svg = useMakeSelector(makeGetCustomSwitchGraphic, capPartNumber)

  useEffect(() => {
    if (!base64Svg && capPartNumber) {
      doFetch({ capPartNumber })
    }
  }, [base64Svg, doFetch, capPartNumber])

  const showLoading = !base64Svg && !showPartNumbers && !error
  const { width, height } = dimensions

  if (base64Svg === ERROR && doneLoading && !showPartNumbers) {
    if (isCustomIndicator) {
      return <BlankCustomIndicator style={{ width, height }} />
    }
    if (vehicleSubType === TRUCK_SUBTYPE_42N) {
      return component.locationType === MSF ? (
        <BlankMSF42N style={{ width, height }} />
      ) : (
        <BlankSwitch42N style={{ width, height }} />
      )
    } else {
      return component.locationType === MSF ? (
        <BlankMSF style={{ width, height }} />
      ) : (
        <BlankSwitch style={{ width, height }} />
      )
    }
  }

  if (showLoading)
    return (
      <>
        {showPartNumbers && (
          <PartNumberView
            location={location}
            selected={selected}
            dimensions={dimensions}
          />
        )}
        <LoadingSwitchMeter width={width} height={height} />
      </>
    )

  if (showPartNumbers)
    return <PartNumberView location={location} selected={selected} />

  return (
    <div style={{ position: 'relative' }}>
      <CustomSvg
        base64Svg={base64Svg}
        location={location}
        onClick={onClick}
        selected={selected}
        hasChanged={hasChanged}
        capPartNumber={capPartNumber}
        panelNumber={panelNumber}
        dimensions={dimensions}
        isCustomIndicator={isCustomIndicator}
        showPill={showPill}
        vehicleSubType={vehicleSubType}
      />
    </div>
  )
}

PartGraphicCustom.propTypes = {
  location: PropTypes.string,
  capPartNumber: PropTypes.string,
  onClick: PropTypes.func,
  selected: PropTypes.bool,
  hasChanged: PropTypes.bool,
  dimensions: PropTypes.object,
  isCustomIndicator: PropTypes.bool,
  panelNumber: PropTypes.string,
  showPill: PropTypes.bool,
  vehicleSubType: PropTypes.string,
}

export default PartGraphicCustom

export const CustomSvg = ({
  base64Svg,
  location,
  capPartNumber,
  dimensions,
  isCustomIndicator,
  panelNumber,
  showPill = true,
  vehicleSubType: vehicleSubTypeFromLineNote,
}) => {
  const { width, height } = dimensions
  const colorBase = convertPartNumberToColorBase(panelNumber)
  const vehicleSubTypeFromDash = useSelector(getVehicleSubType)
  const vehicleSubType = vehicleSubTypeFromLineNote || vehicleSubTypeFromDash

  return (
    <SwitchContainer>
      <Wrap>
        {base64Svg && isCustomIndicator && (
          <MaskedSVG
            svg={base64Svg}
            width={width}
            height={height}
            capPartNumber={capPartNumber}
            panelNumber={panelNumber}
            alt={`${location} cap part number ${capPartNumber}`}
          />
        )}
        {base64Svg && !isCustomIndicator && (
          <img
            src={`data:image/svg+xml;base64,${base64Svg}`}
            width={width}
            height={height}
            alt={`${location} cap part number ${capPartNumber}`}
          />
        )}
      </Wrap>
      {isCustomIndicator && (
        <BlankCustomIndicator key={location} style={{ width, height }} />
      )}
      {!isCustomIndicator && (
        <>
          {vehicleSubType === TRUCK_SUBTYPE_42N ? (
            <BlankSwitch42N key={location} style={{ width, height }} />
          ) : (
            <BlankSwitch key={location} style={{ width, height }} />
          )}
        </>
      )}
      {colorBase && showPill && <StyledPill>{colorBase}</StyledPill>}
    </SwitchContainer>
  )
}

CustomSvg.propTypes = {
  base64Svg: PropTypes.string.isRequired,
  location: PropTypes.string,
  onClick: PropTypes.func,
  selected: PropTypes.bool,
  hasChanged: PropTypes.bool,
  capPartNumber: PropTypes.string,
  dimensions: PropTypes.object,
  isCustomIndicator: PropTypes.bool,
  panelNumber: PropTypes.string,
  showPill: PropTypes.bool,
  vehicleSubType: PropTypes.string,
}

export const StyledLoadingMeter = styled(LoadingMeter)`
  position: absolute !important;
  bottom: 2px;
  left: 2px;
  z-index: 30;
`

const Wrap = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 20;
`

const SwitchContainer = styled.div`
  && div {
    position: absolute !important;
  }
`

const StyledPill = styled(Pill)`
  background-color: black;
  position: absolute;
  bottom: 6px;
  right: 0;
  z-index: 20;
  width: 50px;
  height: 18px;
  font-size: 17px;
  font-weight: bold;
  color: ${themeColor('whitePressed')};
`

const MaskedSVG = styled.div((props) => {
  if (props.svg === undefined) console.error('no svg', props)

  const url = `data:image/svg+xml;base64,${props.svg}`
  const color = {
    [RED]: '#ee4b4e',
    [AMBER]: '#fa8147',
    [GREEN]: '#09bc63',
    undefined: '#fff',
  }
  const colorBase = convertPartNumberToColorBase(props.panelNumber)

  return `
    background-color: ${color[colorBase]};
    mask-image: url(${url});
    mask-repeat: no-repeat;
    mask-size: contain;
    width: ${props.width}px;
    height: ${props.height}px;
  `
})

const RED = 'RED'
const AMBER = 'AMB'
const GREEN = 'GRN'

function convertPartNumberToColorBase(partNumber) {
  const colors = [
    [/A66-19843-00[0-2]/, RED],
    [/A66-19843-00[3-5]/, AMBER],
    [/A66-19843-00[6-8]/, GREEN],
  ]

  const [[, color] = []] = colors.filter(([matcher]) =>
    matcher.test(partNumber)
  )

  return color
}
